import { useState, useEffect, useCallback } from 'react';
import { KeyRound, Plus, Eye, EyeOff, Copy, Check, Trash2, Ban, AlertTriangle, RefreshCw } from 'lucide-react';
import { usePermissions } from '../context/PermissionsContext';
import apiTokensAPI from '../api/apiTokens';

// ── Scope metadata ─────────────────────────────────────────────────────────────
const SCOPES = [
  { value: 'webhook',          label: 'Webhook',          description: 'Webhook routing — use in any webhook URL to identify your company' },
  { value: 'api:read',         label: 'API Read',          description: 'Read-only external API access' },
  { value: 'api:write',        label: 'API Write',         description: 'Full external API access (read + write)' },
  { value: 'integration:sync', label: 'Integration Sync',  description: 'Trigger ERP sync operations' },
];

// ── Status badge ───────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    Active:  'bg-green-100 text-green-800',
    Expired: 'bg-yellow-100 text-yellow-800',
    Revoked: 'bg-gray-100 text-gray-600',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
}

// ── Date display ───────────────────────────────────────────────────────────────
function FmtDate({ value, fallback = '—' }) {
  if (!value) return <span className="text-gray-400">{fallback}</span>;
  return <span>{new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</span>;
}

// ── Copy-to-clipboard button ───────────────────────────────────────────────────
function CopyButton({ text, className = '' }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard may not be available in some contexts
    }
  };
  return (
    <button
      type="button"
      onClick={doCopy}
      title={copied ? 'Copied!' : 'Copy to clipboard'}
      className={`p-1 rounded hover:bg-gray-100 transition-colors ${className}`}
    >
      {copied ? <Check size={16} className="text-green-600" /> : <Copy size={16} className="text-gray-500" />}
    </button>
  );
}

// ── Confirm dialog ─────────────────────────────────────────────────────────────
function ConfirmDialog({ open, title, message, confirmLabel = 'Confirm', danger = false, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">{title}</h2>
        <p className="text-sm text-gray-600 mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-sm rounded border border-gray-300 hover:bg-gray-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`px-4 py-2 text-sm rounded text-white ${danger ? 'bg-red-600 hover:bg-red-700' : 'bg-primary-600 hover:bg-primary-700'}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── New-token reveal panel ─────────────────────────────────────────────────────
function NewTokenReveal({ tokenData, onDismiss }) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 mb-6">
      <div className="flex items-start gap-3 mb-3">
        <AlertTriangle size={20} className="text-amber-600 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-semibold text-amber-800">Save your token now — it will never be shown again</p>
          <p className="text-xs text-amber-700 mt-0.5">
            Copy it to a password manager or secrets store before closing this notice.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 bg-white border border-amber-200 rounded px-3 py-2">
        <code className="flex-1 text-sm font-mono break-all select-all">
          {visible ? tokenData.token : '•'.repeat(Math.min(tokenData.token.length, 48))}
        </code>
        <button
          type="button"
          onClick={() => setVisible(v => !v)}
          title={visible ? 'Hide token' : 'Show token'}
          className="p-1 rounded hover:bg-gray-100 transition-colors shrink-0"
        >
          {visible ? <EyeOff size={16} className="text-gray-500" /> : <Eye size={16} className="text-gray-500" />}
        </button>
        <CopyButton text={tokenData.token} />
      </div>

      <div className="mt-3 text-xs text-amber-700 space-y-0.5">
        <div><span className="font-medium">Name:</span> {tokenData.name}</div>
        <div><span className="font-medium">Scope:</span> {tokenData.scope}</div>
        {tokenData.expiresAt && (
          <div><span className="font-medium">Expires:</span> {new Date(tokenData.expiresAt).toLocaleDateString()}</div>
        )}
      </div>

      <button
        type="button"
        onClick={onDismiss}
        className="mt-4 text-xs text-amber-700 underline hover:text-amber-900"
      >
        I've saved my token — dismiss
      </button>
    </div>
  );
}

// ── Create-token modal ─────────────────────────────────────────────────────────
function CreateTokenModal({ open, onClose, onCreate }) {
  const [name, setName]         = useState('');
  const [scope, setScope]       = useState('webhook');
  const [noExpiry, setNoExpiry] = useState(true);
  const [expiresAt, setExpires] = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  const reset = () => { setName(''); setScope('webhook'); setNoExpiry(true); setExpires(''); setError(''); };

  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError('Token name is required'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: name.trim(),
        scope,
        expiresAt: noExpiry ? null : (expiresAt || null),
      };
      const res = await onCreate(payload);
      reset();
      onClose(res);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create token');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg mx-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Create API Token</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Token name *</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Xero Webhook Production"
              maxLength={100}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Scope */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Scope *</label>
            <select
              value={scope}
              onChange={e => setScope(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {SCOPES.map(s => (
                <option key={s.value} value={s.value}>{s.label} — {s.description}</option>
              ))}
            </select>
          </div>

          {/* Expiry */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expiry</label>
            <div className="flex items-center gap-3 mb-2">
              <input
                type="checkbox"
                id="no-expiry"
                checked={noExpiry}
                onChange={e => setNoExpiry(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label htmlFor="no-expiry" className="text-sm text-gray-700">Never expires</label>
            </div>
            {!noExpiry && (
              <input
                type="date"
                value={expiresAt}
                min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                onChange={e => setExpires(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            )}
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={handleClose} className="px-4 py-2 text-sm rounded border border-gray-300 hover:bg-gray-50">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm rounded bg-primary-600 hover:bg-primary-700 text-white disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? <RefreshCw size={14} className="animate-spin" /> : <Plus size={14} />}
              {saving ? 'Creating…' : 'Create Token'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function ApiTokensSettings() {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('manage_api_tokens');

  const [tokens, setTokens]               = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState('');

  const [showCreate, setShowCreate]       = useState(false);
  const [newTokenData, setNewTokenData]   = useState(null);  // shown after creation

  const [confirmDialog, setConfirmDialog] = useState(null);   // { type, tokenId, tokenName }

  const loadTokens = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiTokensAPI.getAll();
      setTokens(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load tokens');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTokens(); }, [loadTokens]);

  // ── Create ──────────────────────────────────────────────────────────────────
  const handleCreate = async (payload) => {
    const res = await apiTokensAPI.create(payload);
    return res.data;
  };

  const handleCreateClose = (result) => {
    setShowCreate(false);
    if (result) {
      setNewTokenData(result);
      loadTokens();
    }
  };

  // ── Revoke ──────────────────────────────────────────────────────────────────
  const openRevoke = (token) => setConfirmDialog({ type: 'revoke', tokenId: token.TokenID, tokenName: token.TokenName });
  const confirmRevoke = async () => {
    const { tokenId } = confirmDialog;
    setConfirmDialog(null);
    try {
      await apiTokensAPI.revoke(tokenId);
      loadTokens();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to revoke token');
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────
  const openDelete = (token) => setConfirmDialog({ type: 'delete', tokenId: token.TokenID, tokenName: token.TokenName });
  const confirmDelete = async () => {
    const { tokenId } = confirmDialog;
    setConfirmDialog(null);
    try {
      await apiTokensAPI.delete(tokenId);
      loadTokens();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete token');
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <KeyRound size={24} className="text-primary-600" />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">API Tokens</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Personal access tokens for external integrations, webhooks, and automated tools.
            </p>
          </div>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm rounded-md transition-colors"
          >
            <Plus size={16} />
            New Token
          </button>
        )}
      </div>

      {/* New-token reveal */}
      {newTokenData && (
        <NewTokenReveal tokenData={newTokenData} onDismiss={() => setNewTokenData(null)} />
      )}

      {/* Error */}
      {error && (
        <div className="mb-4 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw size={24} className="animate-spin text-gray-400" />
        </div>
      ) : tokens.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <KeyRound size={40} className="mx-auto mb-3 text-gray-300" />
          <p className="font-medium">No API tokens yet</p>
          {canManage && (
            <p className="text-sm mt-1">
              Click <strong>New Token</strong> to create one.
            </p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Scope</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Prefix</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Expires</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Last Used</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Created By</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                {canManage && (
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {tokens.map(tok => (
                <tr key={tok.TokenID} className={tok.Status !== 'Active' ? 'opacity-60' : ''}>
                  <td className="px-4 py-3 font-medium text-gray-900">{tok.TokenName}</td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">
                      {tok.Scope}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{tok.TokenPrefix}…</td>
                  <td className="px-4 py-3 text-gray-600">
                    <FmtDate value={tok.ExpiresAt} fallback="Never" />
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    <FmtDate value={tok.LastUsedAt} fallback="Never" />
                  </td>
                  <td className="px-4 py-3 text-gray-600">{tok.CreatedByName || '—'}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={tok.Status} />
                  </td>
                  {canManage && (
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        {tok.Status === 'Active' && (
                          <button
                            type="button"
                            onClick={() => openRevoke(tok)}
                            title="Revoke token"
                            className="p-1.5 rounded text-yellow-600 hover:bg-yellow-50 transition-colors"
                          >
                            <Ban size={15} />
                          </button>
                        )}
                        {tok.Status === 'Revoked' && (
                          <button
                            type="button"
                            onClick={() => openDelete(tok)}
                            title="Delete token permanently"
                            className="p-1.5 rounded text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create modal */}
      <CreateTokenModal
        open={showCreate}
        onClose={handleCreateClose}
        onCreate={handleCreate}
      />

      {/* Confirm dialogs */}
      <ConfirmDialog
        open={confirmDialog?.type === 'revoke'}
        title="Revoke Token"
        message={`Revoke "${confirmDialog?.tokenName}"? Any integrations using this token will immediately stop working. This cannot be undone.`}
        confirmLabel="Revoke"
        danger
        onConfirm={confirmRevoke}
        onCancel={() => setConfirmDialog(null)}
      />
      <ConfirmDialog
        open={confirmDialog?.type === 'delete'}
        title="Delete Token"
        message={`Permanently delete "${confirmDialog?.tokenName}"? This removes the audit record and cannot be undone.`}
        confirmLabel="Delete"
        danger
        onConfirm={confirmDelete}
        onCancel={() => setConfirmDialog(null)}
      />
    </div>
  );
}
